import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Globe, Lock, Share2, Pencil, Loader2, FileText, Video, Music, Image as ImageIcon } from "lucide-react";

const EMOJI_CHOICES = ["🌱", "🌊", "🔥", "✨", "🌙", "🌸", "🧘", "💪", "🎯", "📚"];
const COLOR_CHOICES = ["#3E7C6A", "#7A5C3B", "#A64F5C", "#4B4B7A", "#2E7BA6", "#8A6BAE"];
const TYPE_ICON = { video: Video, audio: Music, image: ImageIcon };

export default function CollectionDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("collections_title"));
  const [folder, setFolder] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: folders } = await api.get("/bookmarks/folders");
      setFolder((folders || []).find((x) => x.id === id) || null);
      const { data: b } = await api.get("/bookmarks", { params: { folder_id: id } });
      setPosts(Array.isArray(b) ? b : []);
    } catch { setFolder(null); setPosts([]); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const shareUrl = useMemo(() => (folder?.share_slug ? `${API_BASE}/public/collections/${folder.share_slug}` : null), [folder]);
  const accent = folder?.color || "#3E7C6A";

  const toggleShare = async () => {
    if (!folder) return;
    setBusy(true);
    try {
      if (folder.is_public) {
        await api.post(`/bookmarks/folders/${folder.id}/unshare`);
        toast.success(t("collections_now_private"), { description: t("collections_now_private_body") });
      } else {
        const { data: r } = await api.post(`/bookmarks/folders/${folder.id}/share`);
        const url = `${API_BASE}/public/collections/${r?.slug}`;
        try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
        toast.success(t("collections_now_public"), { description: t("collections_now_public_body") });
      }
      await load();
    } catch (e) { toast.error(apiError(e, t("action_failed"))); } finally { setBusy(false); }
  };

  const reshare = async () => {
    if (!shareUrl || !folder) return;
    const msg = t("collections_share_msg", { name: folder.name, url: shareUrl });
    try {
      if (navigator.share) await navigator.share({ title: folder.name, text: msg, url: shareUrl });
      else { await navigator.clipboard.writeText(shareUrl); toast.success(t("collections_share_again")); }
    } catch { /* cancelled */ }
  };

  const saveEdit = async () => {
    if (!draft || !folder) return;
    setBusy(true);
    try {
      await api.patch(`/bookmarks/folders/${folder.id}`, {
        name: draft.name, emoji: draft.emoji ?? "", color: draft.color ?? "", description: draft.description ?? "",
      });
      setEditOpen(false);
      await load();
    } catch (e) { toast.error(apiError(e, t("action_failed"))); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!folder) {
    return (
      <div className="mx-auto max-w-2xl">
        <button onClick={() => navigate("/app/collections")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <p className="py-16 text-center text-sm text-muted-foreground">{t("collections_not_found")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="collection-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collections")} data-testid="collection-back" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="flex-1 truncate font-display text-2xl text-foreground">{folder.emoji ? `${folder.emoji}  ` : ""}{folder.name}</h1>
        <button onClick={() => { setDraft({ ...folder }); setEditOpen(true); }} data-testid="collection-edit" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><Pencil className="h-4 w-4" /></button>
      </div>

      <div className="rounded-2xl p-5 text-white" style={{ backgroundColor: accent }}>
        {folder.emoji ? <p className="text-4xl">{folder.emoji}</p> : null}
        <p className="mt-1 font-display text-2xl">{folder.name}</p>
        {folder.description ? <p className="mt-1 text-sm text-white/85">{folder.description}</p> : null}
        <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-white/70">{t("collections_n_posts", { n: posts.length })}</p>
      </div>

      <div className="flex gap-2">
        <button onClick={toggleShare} disabled={busy} data-testid="collection-share-toggle"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium ${folder.is_public ? "bg-primary text-primary-foreground" : "border border-border text-foreground hover:bg-accent"}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : folder.is_public ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          {folder.is_public ? t("collections_public") : t("collections_private")}
        </button>
        {folder.is_public && shareUrl ? (
          <button onClick={reshare} data-testid="collection-reshare" className="inline-flex items-center gap-1.5 rounded-full border border-primary bg-primary/10 px-3 py-2 text-sm font-medium text-primary">
            <Share2 className="h-3.5 w-3.5" /> {t("collections_share_again")}
          </button>
        ) : null}
      </div>

      {posts.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t("collections_empty_folder")}</p>
      ) : (
        <div className="space-y-2.5">
          {posts.map((p) => {
            const Icon = TYPE_ICON[p.content_type] || FileText;
            return (
              <button key={p.id} data-testid={`collection-post-${p.id}`} onClick={() => navigate(`/app/feed/post/${p.id}`)}
                className="flex w-full items-center gap-3 rounded-xl bg-secondary/60 p-3 text-left transition-colors hover:bg-accent">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15"><Icon className="h-4 w-4 text-primary" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground">{p.title || t("post_untitled")}</span>
                  <span className="block truncate text-xs text-muted-foreground">{p.coach_name || p.author_name}{p.is_paid ? ` · ${t("collections_paid_tag")}` : ""}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}

      {editOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setEditOpen(false)}>
          <div className="w-full max-w-md rounded-t-2xl bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-lg text-foreground">{t("collections_edit_title")}</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("collections_field_name")}</label>
                <input data-testid="collection-edit-name" value={draft?.name || ""} maxLength={60} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className="w-full rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("collections_field_description")}</label>
                <textarea data-testid="collection-edit-desc" value={draft?.description || ""} maxLength={200} placeholder={t("collections_field_description_ph")} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className="min-h-[60px] w-full rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground outline-none" />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("collections_field_emoji")}</label>
                <div className="flex flex-wrap gap-2">
                  {EMOJI_CHOICES.map((e) => (
                    <button key={e} data-testid={`collection-emoji-${e}`} onClick={() => setDraft((d) => ({ ...d, emoji: d?.emoji === e ? null : e }))}
                      className={`flex h-10 w-10 items-center justify-center rounded-full border text-xl ${draft?.emoji === e ? "border-primary bg-primary/15" : "border-transparent bg-secondary/60"}`}>{e}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("collections_field_color")}</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_CHOICES.map((c) => (
                    <button key={c} data-testid={`collection-color-${c}`} onClick={() => setDraft((d) => ({ ...d, color: d?.color === c ? null : c }))}
                      className={`h-8 w-8 rounded-full border-2 ${draft?.color === c ? "border-foreground" : "border-transparent"}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setEditOpen(false)} className="flex-1 rounded-full bg-secondary/60 py-2.5 text-sm font-medium text-foreground">{t("cancel")}</button>
              <button onClick={saveEdit} disabled={busy} data-testid="collection-edit-save" className="flex flex-1 items-center justify-center rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("save")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
